import React, { createContext, useContext, useState, useEffect } from 'react';

// Create Theme Context
const ThemeContext = createContext();

// Theme hook for easy access
export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

// Theme Provider Component
export const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState('dark'); // Default to dark theme
  const [isTransitioning, setIsTransitioning] = useState(false);

  // Load saved theme from localStorage on mount
  useEffect(() => {
    const savedTheme = localStorage.getItem('music-portfolio-theme');
    if (savedTheme && ['light', 'dark'].includes(savedTheme)) {
      setTheme(savedTheme);
    }
  }, []);

  // Apply theme to document root and save to localStorage
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    localStorage.setItem('music-portfolio-theme', theme);
    
    // Add theme class to body for additional styling
    document.body.className = document.body.className.replace(/theme-\w+/g, '');
    document.body.classList.add(`theme-${theme}`);
  }, [theme]);

  // Toggle theme function with smooth transition
  const toggleTheme = () => {
    setIsTransitioning(true);
    
    // Add transition class for smooth animation
    document.documentElement.classList.add('theme-transitioning');
    
    setTimeout(() => {
      setTheme(prevTheme => prevTheme === 'dark' ? 'light' : 'dark');
      
      setTimeout(() => {
        setIsTransitioning(false);
        document.documentElement.classList.remove('theme-transitioning');
      }, 300);
    }, 150);
  };

  // Get current theme info
  const isDarkMode = theme === 'dark';
  const isLightMode = theme === 'light';

  const value = {
    theme,
    isDarkMode,
    isLightMode,
    toggleTheme,
    isTransitioning,
    setTheme
  };

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  );
};
